import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BookOpen } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import GoogleButton from "@/components/GoogleButton";
import { api, errorMessage } from "@/lib/api";

export default function RegisterPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const preSlug = params.get("module") || "";

  const [form, setForm] = useState({ full_name: "", email: "", password: "", mobile: "", organization: "" });
  const [modules, setModules] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(preSlug);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/modules").then((r) => setModules(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (preSlug) setSelectedSlug(preSlug);
  }, [preSlug]);

  const selectedModule = modules.find((m) => m.slug === selectedSlug);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.mobile) delete payload.mobile;
      if (!payload.organization) delete payload.organization;
      if (selectedSlug) payload.requested_module = selectedSlug;
      await api.post("/auth/register", payload);
      nav("/awaiting-approval", { state: { email: form.email, name: form.full_name, module: selectedModule?.name } });
    } catch (err) {
      setError(errorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell
      title="Request access."
      subtitle="Create account"
      footer={<div>Already have an account? <Link to="/login" className="link-underline font-semibold text-foreground">Sign in</Link></div>}
    >
      {selectedModule && (
        <div data-testid="selected-module-banner" className="mb-6 p-4 border border-primary/30 bg-primary/5">
          <div className="flex items-start gap-3">
            <BookOpen className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="overline text-primary">Requesting access to</div>
              <div className="font-display font-bold text-[15px] mt-1">{selectedModule.name}</div>
              <div className="text-[12px] text-muted-foreground mt-1">One module per request. You can request additional tracks from your dashboard after approval.</div>
            </div>
          </div>
        </div>
      )}

      <GoogleButton />
      <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        <div className="flex-1 h-px bg-border" /> or with email <div className="flex-1 h-px bg-border" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Full name *</label>
          <input data-testid="register-name-input" required minLength={2} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="w-full h-11 px-3 border border-border bg-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
        </div>
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Email *</label>
          <input data-testid="register-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full h-11 px-3 border border-border bg-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
        </div>
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Password *</label>
          <input data-testid="register-password-input" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full h-11 px-3 border border-border bg-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
          <div className="text-[11px] text-muted-foreground mt-1">Minimum 6 characters.</div>
        </div>
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
            Training track you want to learn
          </label>
          <select
            data-testid="register-module-select"
            value={selectedSlug}
            onChange={(e) => setSelectedSlug(e.target.value)}
            className="w-full h-11 px-3 border border-border bg-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-[13.5px]"
          >
            <option value="">-- Select a track --</option>
            {modules.map((m) => (
              <option key={m.slug} value={m.slug}>{m.name}</option>
            ))}
          </select>
          <div className="text-[11px] text-muted-foreground mt-1">You can only request one module at a time. Request more after approval.</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Mobile</label>
            <input data-testid="register-mobile-input" type="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              className="w-full h-11 px-3 border border-border bg-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
          </div>
          <div>
            <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Organization / University</label>
            <input data-testid="register-org-input" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })}
              className="w-full h-11 px-3 border border-border bg-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
          </div>
        </div>
        {error && <div data-testid="register-error" className="text-[13px] text-destructive">{error}</div>}
        <button data-testid="register-submit-button" disabled={loading} type="submit"
          className="w-full h-11 bg-primary text-primary-foreground font-semibold text-[14px] hover:bg-[#001d6b] disabled:opacity-60">
          {loading ? "Submitting..." : "Submit for approval"}
        </button>
        <div className="text-[12px] text-muted-foreground">
          By submitting, you agree your account will be reviewed by a Steadfast administrator. You'll be notified by email.
        </div>
      </form>
    </AuthShell>
  );
}
